import type { Card } from '@campfire/cards';
import { ACE_LOW_RUNS, candidateMelds, layoffOptions, orderMeld, pipValue, type RunRules } from './meld.ts';

export interface Layoff {
  /** Index into `layoffOnto`. */
  readonly target: number;
  readonly cards: readonly Card[];
}

export interface Arrangement {
  /** Melds made from the hand, each in display order. */
  readonly melds: readonly (readonly Card[])[];
  /** Cards laid off on someone else's melds (only when `layoffOnto` was given). */
  readonly layoffs: readonly Layoff[];
  readonly deadwood: readonly Card[];
  /** Sum of `value` over the deadwood. */
  readonly points: number;
}

export interface ArrangeOptions {
  readonly rules?: RunRules;
  /** Deadwood value of a card. Defaults to pipValue (ace 1, faces 10). */
  readonly value?: (c: Card) => number;
  /** Opponent melds the hand may lay off on (Gin defender). Each may be extended once, at one or both ends. */
  readonly layoffOnto?: readonly (readonly Card[])[];
}

interface Cand {
  readonly mask: bigint;
  readonly cards: readonly Card[];
  readonly target: number;
}

/**
 * Exact best arrangement of a hand into disjoint melds (plus optional layoffs) that minimises
 * deadwood points (FR-MELD-03). Branches on the lowest undecided card: leave it as deadwood,
 * or use it in one of the melds whose lowest card it is. Memoised on the cards still free.
 */
export function bestArrangement(hand: readonly Card[], opts: ArrangeOptions = {}): Arrangement {
  const rules = opts.rules ?? ACE_LOW_RUNS;
  const value = opts.value ?? pipValue;
  const cards = [...hand];
  const index = new Map(cards.map((c, i) => [c.id, i]));
  const maskOf = (cs: readonly Card[]) => cs.reduce((m, c) => m | (1n << BigInt(index.get(c.id) as number)), 0n);
  const lowest = (m: bigint) => {
    let i = 0;
    while (!((m >> BigInt(i)) & 1n)) i++;
    return i;
  };
  const byLow: Cand[][] = cards.map(() => []);
  const add = (cs: readonly Card[], target: number) => {
    const mask = maskOf(cs);
    byLow[lowest(mask)]?.push({ mask, cards: target < 0 ? orderMeld(cs, rules) : cs, target });
  };
  for (const m of candidateMelds(cards, rules)) add(m, -1);
  (opts.layoffOnto ?? []).forEach((meld, t) => {
    for (const group of layoffOptions(cards, meld, rules)) add(group, t);
  });

  interface Best {
    readonly points: number;
    readonly picks: readonly Cand[];
  }
  const memo = new Map<string, Best>();
  const search = (i: number, used: bigint, targets: number): Best => {
    while (i < cards.length && (used >> BigInt(i)) & 1n) i++;
    if (i >= cards.length) return { points: 0, picks: [] };
    const key = `${i}:${(used >> BigInt(i)).toString(36)}:${targets}`;
    const hit = memo.get(key);
    if (hit) return hit;
    const skip = search(i + 1, used, targets);
    let best: Best = { points: skip.points + value(cards[i] as Card), picks: skip.picks };
    for (const cand of byLow[i] as Cand[]) {
      if (cand.mask & used) continue;
      if (cand.target >= 0 && (targets >> cand.target) & 1) continue;
      const sub = search(i + 1, used | cand.mask, cand.target >= 0 ? targets | (1 << cand.target) : targets);
      if (sub.points < best.points) best = { points: sub.points, picks: [cand, ...sub.picks] };
    }
    memo.set(key, best);
    return best;
  };
  const best = search(0, 0n, 0);
  const covered = best.picks.reduce((m, p) => m | p.mask, 0n);
  return {
    melds: best.picks.filter((p) => p.target < 0).map((p) => p.cards),
    layoffs: best.picks.filter((p) => p.target >= 0).map((p) => ({ target: p.target, cards: p.cards })),
    deadwood: cards.filter((_, i) => !((covered >> BigInt(i)) & 1n)),
    points: best.points,
  };
}

/** Deadwood points of the best arrangement. */
export const deadwood = (hand: readonly Card[], opts: ArrangeOptions = {}): number => bestArrangement(hand, opts).points;
